/* Warehouse / Coletare — Manager dashboard.
 *
 * 3-column layout:
 *  - LEFT: Task list with status pills + filters + KPI cards.
 *  - CENTER: Selected task detail OR "create new" form.
 *  - RIGHT: Settings panel (TV link + token + rotate).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useRealtimeEvent } from "@/lib/realtime-context";
import {
  listTasks, createTask, updateTask, deleteTask,
  searchProducts, getTvConfig, rotateTvToken, getAnalytics,
  managerImageUrl,
} from "@/lib/warehouse";
import { fmtDateTz, APP_TZ } from "@/lib/format";
import { toZonedTime, format as fmtTz } from "date-fns-tz";
import { ro } from "date-fns/locale";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Tv, RotateCw, Copy, ExternalLink, ImagePlus, Trash2, Plus, Search,
  PackageCheck, Loader2, AlarmClock, Clock, CheckCircle2, X, CalendarDays,
} from "lucide-react";

const STATUS_META = {
  pending:     { label: "În așteptare", color: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
  in_progress: { label: "În colectare", color: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  completed:   { label: "Finalizat",    color: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  cancelled:   { label: "Anulat",       color: "bg-rose-100 text-rose-800 border-rose-200", dot: "bg-rose-500" },
};

const PRIORITY_META = {
  urgent: { label: "URGENT", color: "bg-rose-600 text-white border-rose-700" },
  high:   { label: "Mare",   color: "bg-orange-500 text-white border-orange-600" },
  normal: { label: "Normal", color: "bg-slate-100 text-slate-700 border-slate-200" },
  low:    { label: "Mic",    color: "bg-slate-50 text-slate-500 border-slate-200" },
};

// ----- date grouping helpers (Europe/Bucharest) -----

function _localDate(iso) {
  if (!iso) return null;
  try { return toZonedTime(typeof iso === "string" ? new Date(iso) : iso, APP_TZ); }
  catch { return null; }
}

function _dayKey(iso) {
  const d = _localDate(iso);
  if (!d) return "unknown";
  return fmtTz(d, "yyyy-MM-dd", { timeZone: APP_TZ });
}

function _dayLabel(key) {
  if (key === "unknown") return "Necunoscut";
  const today = fmtTz(new Date(), "yyyy-MM-dd", { timeZone: APP_TZ });
  const yesterday = fmtTz(new Date(Date.now() - 86400000), "yyyy-MM-dd", { timeZone: APP_TZ });
  if (key === today) return "Astăzi";
  if (key === yesterday) return "Ieri";
  try {
    const d = new Date(`${key}T12:00:00Z`);
    return fmtTz(d, "EEEE, d MMMM yyyy", { timeZone: APP_TZ, locale: ro });
  } catch { return key; }
}

function _withinRange(iso, rangeKey) {
  if (rangeKey === "all") return true;
  const d = _localDate(iso);
  if (!d) return false;
  const today = fmtTz(new Date(), "yyyy-MM-dd", { timeZone: APP_TZ });
  const yesterday = fmtTz(new Date(Date.now() - 86400000), "yyyy-MM-dd", { timeZone: APP_TZ });
  const dKey = fmtTz(d, "yyyy-MM-dd", { timeZone: APP_TZ });
  if (rangeKey === "today") return dKey === today;
  if (rangeKey === "yesterday") return dKey === yesterday;
  if (rangeKey === "week") {
    // Last 7 days (rolling, inclusive of today)
    const cutoff = new Date(Date.now() - 7 * 86400000);
    return d >= cutoff;
  }
  if (rangeKey === "month") {
    // Same calendar month in local TZ
    const now = new Date();
    return fmtTz(now, "yyyy-MM", { timeZone: APP_TZ }) === fmtTz(d, "yyyy-MM", { timeZone: APP_TZ });
  }
  return true;
}

export default function WarehousePage() {
  const [tasks, setTasks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [filter, setFilter] = useState("active"); // active = pending+in_progress
  const [completedRange, setCompletedRange] = useState("today"); // today/yesterday/week/month/all — only used when filter==="completed"
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // task or "new" or null
  const [tvConfig, setTvConfig] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam =
        filter === "active" ? "pending,in_progress"
        : filter === "all" ? null
        : filter;
      // Fetch more rows for the completed calendar view so we can group several weeks at a glance.
      const limit = filter === "completed" ? 500 : 200;
      const [list, a] = await Promise.all([
        listTasks({ status: statusParam, limit }),
        getAnalytics().catch(() => null),
      ]);
      setTasks(list.items || []);
      if (a) setAnalytics(a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { getTvConfig().then(setTvConfig).catch(() => {}); }, []);

  // Realtime — refresh on any warehouse event.
  useRealtimeEvent("warehouse.task_created", () => reload(), [reload]);
  useRealtimeEvent("warehouse.task_updated", () => reload(), [reload]);
  useRealtimeEvent("warehouse.task_completed", () => reload(), [reload]);
  useRealtimeEvent("warehouse.task_deleted", () => reload(), [reload]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = tasks;
    if (s) {
      list = list.filter((t) => {
        const title = (t.product?.title || t.product_title_snapshot || "").toLowerCase();
        const label = (t.product?.warehouse_label || "").toLowerCase();
        return title.includes(s) || label.includes(s) || (t.notes || "").toLowerCase().includes(s);
      });
    }
    if (filter === "completed") {
      list = list.filter((t) => _withinRange(t.completed_at || t.updated_at, completedRange));
    }
    return list;
  }, [tasks, search, filter, completedRange]);

  // Group completed tasks by day (newest first). Returns an array of {key, label, items, qtySum}.
  const completedGroups = useMemo(() => {
    if (filter !== "completed") return null;
    const groups = new Map();
    for (const t of filtered) {
      const key = _dayKey(t.completed_at || t.updated_at);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    }
    const out = Array.from(groups.entries()).map(([key, items]) => {
      items.sort((a, b) => (b.completed_at || b.updated_at || "").localeCompare(a.completed_at || a.updated_at || ""));
      return {
        key,
        label: _dayLabel(key),
        items,
        qtySum: items.reduce((s, t) => s + (t.quantity || 0), 0),
      };
    });
    // Newest day first; "unknown" goes to the bottom.
    out.sort((a, b) => {
      if (a.key === "unknown") return 1;
      if (b.key === "unknown") return -1;
      return b.key.localeCompare(a.key);
    });
    return out;
  }, [filter, filtered]);

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-background" data-testid="warehouse-page">
      {/* LEFT — list */}
      <div className="w-[420px] border-r border-border flex flex-col bg-card">
        <div className="p-4 space-y-3 border-b border-border">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h1 className="text-base font-semibold flex items-center gap-2">
                <PackageCheck className="h-4 w-4 text-primary" /> Coletare
              </h1>
              <Link
                to="/coletare/imagini"
                className="text-[11px] px-2 py-0.5 rounded-md border border-border bg-card hover:bg-accent inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition"
                data-testid="warehouse-nav-images"
                title="Gestionează imaginile depozit pentru toate produsele"
              >
                <ImagePlus className="h-3 w-3" /> Imagini
              </Link>
            </div>
            <Button
              size="sm"
              data-testid="warehouse-new-task-btn"
              onClick={() => setSelected("new")}
            >
              <Plus className="h-4 w-4 mr-1" /> Task nou
            </Button>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-4 gap-2">
            <KpiPill label="În așteptare" value={analytics?.pending ?? "—"} accent="text-slate-700" testid="kpi-pending" />
            <KpiPill label="În lucru"     value={analytics?.in_progress ?? "—"} accent="text-amber-600" testid="kpi-in-progress" />
            <KpiPill label="Urgente"      value={analytics?.urgent ?? "—"} accent="text-rose-600" testid="kpi-urgent" />
            <KpiPill label="Azi"          value={analytics?.completed_today ?? "—"} accent="text-emerald-600" testid="kpi-completed-today" />
          </div>

          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="warehouse-search"
              className="pl-8 h-9"
              placeholder="Caută în task-uri…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-1.5">
            {[
              ["active", "Active"],
              ["pending", "În așteptare"],
              ["in_progress", "În lucru"],
              ["completed", "Finalizate"],
              ["all", "Toate"],
            ].map(([key, label]) => (
              <button
                key={key}
                data-testid={`warehouse-filter-${key}`}
                onClick={() => setFilter(key)}
                className={`px-2.5 py-1 rounded-md text-xs border transition ${
                  filter === key
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card hover:bg-accent border-border"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {filter === "completed" && (
            <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border" data-testid="warehouse-completed-range">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mr-1">
                <CalendarDays className="h-3 w-3" /> Perioadă
              </span>
              {[
                ["today", "Azi"],
                ["yesterday", "Ieri"],
                ["week", "Săpt. asta"],
                ["month", "Luna asta"],
                ["all", "Tot"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`warehouse-range-${key}`}
                  onClick={() => setCompletedRange(key)}
                  className={`px-2 py-0.5 rounded text-[11px] border transition ${
                    completedRange === key
                      ? "bg-emerald-600 text-white border-emerald-700"
                      : "bg-card hover:bg-accent border-border text-muted-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && tasks.length === 0 ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {filter === "completed" ? "Niciun task finalizat în perioada selectată." : "Nu sunt task-uri."}
            </div>
          ) : completedGroups ? (
            <div data-testid="warehouse-completed-grouped">
              {completedGroups.map((g) => (
                <section key={g.key}>
                  <div className="sticky top-0 z-10 bg-emerald-50/95 dark:bg-emerald-950/80 backdrop-blur border-b border-emerald-200 dark:border-emerald-900 px-3 py-1.5 flex items-baseline justify-between" data-testid={`warehouse-day-header-${g.key}`}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300">{g.label}</span>
                      <span className="text-[10px] text-emerald-700/70 dark:text-emerald-400/70">{g.key !== "unknown" ? fmtTz(new Date(`${g.key}T12:00:00Z`), "dd.MM", { timeZone: APP_TZ }) : ""}</span>
                    </div>
                    <span className="text-[10px] text-emerald-700 dark:text-emerald-400 tabular-nums">
                      {g.items.length} task{g.items.length !== 1 ? "-uri" : ""} · {g.qtySum} buc
                    </span>
                  </div>
                  <ul>
                    {g.items.map((t) => (
                      <TaskRow
                        key={t.id}
                        task={t}
                        selected={selected?.id === t.id}
                        onClick={() => setSelected(t)}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          ) : (
            <ul>
              {filtered.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  selected={selected?.id === t.id}
                  onClick={() => setSelected(t)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* CENTER — detail or create form */}
      <div className="flex-1 overflow-y-auto bg-background">
        {selected === "new" ? (
          <NewTaskForm
            onCancel={() => setSelected(null)}
            onCreated={(t) => { setSelected(t); reload(); }}
          />
        ) : selected ? (
          <TaskDetail
            task={selected}
            onClose={() => setSelected(null)}
            onChanged={reload}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
            Selectează un task din stânga sau apasă „Task nou".
          </div>
        )}
      </div>

      {/* RIGHT — TV settings */}
      <div className="w-[340px] border-l border-border bg-card p-4 space-y-3">
        <TvPanel tvConfig={tvConfig} onRotated={setTvConfig} />
      </div>
    </div>
  );
}

function KpiPill({ label, value, accent = "", testid }) {
  return (
    <div className="rounded-md border border-border bg-card px-2.5 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

function TaskRow({ task, selected, onClick }) {
  const meta = STATUS_META[task.status] || STATUS_META.pending;
  const pri = PRIORITY_META[task.priority] || PRIORITY_META.normal;
  const img = task.product?.image_url;
  return (
    <li>
      <button
        data-testid={`warehouse-task-row-${task.id}`}
        onClick={onClick}
        className={`w-full text-left p-3 border-b border-border flex gap-3 items-center transition ${
          selected ? "bg-primary/5" : "hover:bg-accent"
        }`}
      >
        <div className="h-14 w-14 rounded-md bg-muted flex-shrink-0 overflow-hidden border border-border">
          {img ? (
            <img src={managerImageUrl(img)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImagePlus className="h-5 w-5" /></div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${pri.color}`}>{pri.label}</Badge>
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${meta.color}`}>
              <span className={`h-1.5 w-1.5 rounded-full mr-1 ${meta.dot}`} /> {meta.label}
            </Badge>
          </div>
          <div className="text-sm font-medium line-clamp-1">{task.product?.title || task.product_title_snapshot || "—"}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Cantitate: <span className="font-semibold text-foreground">{task.quantity}</span></div>
        </div>
      </button>
    </li>
  );
}

function TaskDetail({ task, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    quantity: task.quantity,
    priority: task.priority,
    notes: task.notes || "",
  });

  // Re-sync when the selected task changes
  useEffect(() => {
    setDraft({ quantity: task.quantity, priority: task.priority, notes: task.notes || "" });
    setEditing(false);
  }, [task.id]); // eslint-disable-line

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await updateTask(task.id, { status });
      toast.success("Status actualizat");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await updateTask(task.id, { ...draft, quantity: Number(draft.quantity) });
      toast.success("Salvat");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Sigur ștergi acest task?")) return;
    setBusy(true);
    try {
      await deleteTask(task.id);
      toast.success("Șters");
      onClose?.();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const meta = STATUS_META[task.status] || STATUS_META.pending;
  const img = task.product?.image_url;
  const usesSnapshot = task.product?.uses_snapshot;
  const productId = task.product_id;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-5" data-testid="warehouse-task-detail">
      {/* header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <Badge variant="outline" className={meta.color}>{meta.label}</Badge>
          <h2 className="mt-2 text-xl font-semibold leading-tight">{task.product?.title || task.product_title_snapshot}</h2>
          <div className="text-xs text-muted-foreground mt-1">
            Creat de {task.created_by_name || "—"} · {new Date(task.created_at).toLocaleString("ro-RO")}
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="warehouse-task-close"><X className="h-4 w-4" /></Button>
      </div>

      {/* image + quick actions */}
      <div className="flex gap-4">
        <div className="h-40 w-40 rounded-lg bg-muted border border-border overflow-hidden flex-shrink-0 relative group">
          {img ? <img src={managerImageUrl(img)} alt="" className="w-full h-full object-cover" /> : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImagePlus className="h-8 w-8" /></div>
          )}
          {usesSnapshot && (
            <div className="absolute top-1 left-1 text-[9px] uppercase tracking-wider bg-emerald-500 text-white px-1.5 py-0.5 rounded font-semibold">
              SNAPSHOT
            </div>
          )}
          {productId && (
            <Link
              to={`/coletare/imagini?q=${encodeURIComponent(productId)}`}
              className="absolute inset-0 flex items-center justify-center bg-black/55 opacity-0 group-hover:opacity-100 transition text-white text-xs font-medium text-center px-2"
              data-testid="warehouse-go-manage-image"
              title="Gestionează imaginea master pentru acest produs"
            >
              <span className="flex flex-col items-center gap-1"><ImagePlus className="h-4 w-4" /> Gestionează<br/>imaginea master</span>
            </Link>
          )}
        </div>

        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <KV label="Cantitate" value={
              editing ? (
                <Input
                  type="number"
                  min="1"
                  value={draft.quantity}
                  onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                  className="h-8"
                  data-testid="warehouse-edit-quantity"
                />
              ) : <span className="text-xl font-semibold tabular-nums">{task.quantity}</span>
            } />
            <KV label="Prioritate" value={
              editing ? (
                <select
                  value={draft.priority}
                  onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
                  className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                  data-testid="warehouse-edit-priority"
                >
                  <option value="low">Mic</option>
                  <option value="normal">Normal</option>
                  <option value="high">Mare</option>
                  <option value="urgent">URGENT</option>
                </select>
              ) : (
                <Badge variant="outline" className={(PRIORITY_META[task.priority] || PRIORITY_META.normal).color}>
                  {(PRIORITY_META[task.priority] || PRIORITY_META.normal).label}
                </Badge>
              )
            } />
          </div>

          <KV label="Note" value={
            editing ? (
              <Textarea
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                rows={2}
                placeholder="Note pentru picker (opțional)"
                data-testid="warehouse-edit-notes"
              />
            ) : <span className="text-sm text-muted-foreground">{task.notes || "—"}</span>
          } />
        </div>
      </div>

      {/* status flow */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
        {task.status === "pending" && (
          <Button size="sm" variant="outline" onClick={() => setStatus("in_progress")} disabled={busy} data-testid="warehouse-status-in-progress">
            <Clock className="h-4 w-4 mr-1" /> Marchează „În colectare"
          </Button>
        )}
        {task.status !== "completed" && (
          <Button size="sm" onClick={() => setStatus("completed")} disabled={busy} data-testid="warehouse-status-completed">
            <CheckCircle2 className="h-4 w-4 mr-1" /> Marchează finalizat
          </Button>
        )}
        {task.status === "completed" && (
          <Button size="sm" variant="outline" onClick={() => setStatus("pending")} disabled={busy} data-testid="warehouse-status-reopen">
            Redeschide
          </Button>
        )}
        {task.status !== "cancelled" && task.status !== "completed" && (
          <Button size="sm" variant="outline" onClick={() => setStatus("cancelled")} disabled={busy} data-testid="warehouse-status-cancelled">
            Anulează
          </Button>
        )}
        {!editing ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="warehouse-edit-btn">Editează</Button>
        ) : (
          <>
            <Button size="sm" onClick={save} disabled={busy} data-testid="warehouse-save-btn">Salvează</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Renunță</Button>
          </>
        )}
        <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 ml-auto" onClick={remove} disabled={busy} data-testid="warehouse-delete-btn">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">{label}</div>
      <div>{value}</div>
    </div>
  );
}

function NewTaskForm({ onCancel, onCreated }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [priority, setPriority] = useState("normal");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let aborted = false;
    const t = setTimeout(async () => {
      try {
        const r = await searchProducts(q, 20);
        if (!aborted) setResults(r.items || []);
      } catch {}
    }, 250);
    return () => { aborted = true; clearTimeout(t); };
  }, [q]);

  const submit = async () => {
    if (!product) { toast.error("Selectează un produs"); return; }
    setBusy(true);
    try {
      const task = await createTask({
        kind: "product",
        product_id: product.id,
        quantity: Number(quantity),
        priority,
        notes: notes || null,
      });
      toast.success("Task creat");
      onCreated?.(task);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const hasWarehouseImg = !!product?.warehouse_image_url;
  const warehouseThumb = hasWarehouseImg ? managerImageUrl(product.warehouse_image_url) : null;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-5" data-testid="warehouse-new-form">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Task nou de coletare</h2>
        <Button variant="ghost" size="icon" onClick={onCancel}><X className="h-4 w-4" /></Button>
      </div>

      {!product ? (
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="warehouse-product-search"
              className="pl-8"
              placeholder="Caută produs (SKU, titlu, vendor)…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
          </div>
          <ul className="border border-border rounded-md divide-y divide-border max-h-96 overflow-y-auto">
            {results.map((p) => (
              <li key={p.id}>
                <button
                  data-testid={`warehouse-product-${p.id}`}
                  onClick={() => setProduct(p)}
                  className="w-full text-left p-3 hover:bg-accent flex items-center gap-3"
                >
                  <div className="h-10 w-10 rounded bg-muted overflow-hidden border border-border">
                    {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm line-clamp-1">{p.title}</div>
                    <div className="text-xs text-muted-foreground">{p.sku || p.vendor}</div>
                  </div>
                  {p.has_warehouse_image
                    ? <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">Img depozit</Badge>
                    : <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">Fără img</Badge>}
                </button>
              </li>
            ))}
            {results.length === 0 && (
              <li className="p-3 text-sm text-muted-foreground text-center">Niciun produs găsit.</li>
            )}
          </ul>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 border border-border rounded-md bg-card">
            <div className="h-14 w-14 rounded bg-muted overflow-hidden border border-border">
              {product.image_url && <img src={product.image_url} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium line-clamp-2">{product.title}</div>
              <div className="text-xs text-muted-foreground">{product.sku || product.vendor || product.id}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setProduct(null)} data-testid="warehouse-product-change">Schimbă</Button>
          </div>

          {/* Warehouse master image preview — gets snapshotted at create */}
          {hasWarehouseImg ? (
            <div className="flex gap-3 p-3 rounded-md border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/20" data-testid="warehouse-master-preview">
              <img src={warehouseThumb} alt="" className="h-20 w-20 object-cover rounded border border-emerald-200" />
              <div className="flex-1 min-w-0 text-xs">
                <div className="text-emerald-800 dark:text-emerald-300 font-semibold mb-0.5 flex items-center gap-1">
                  <ImagePlus className="h-3.5 w-3.5" /> Imagine depozit setată
                </div>
                <div className="text-muted-foreground">
                  Această imagine va fi <span className="font-medium">snapshotată acum</span> și va rămâne pentru toată viața task-ului. Modificările ulterioare ale imaginii depozit pentru acest produs NU vor afecta task-ul.
                </div>
                {product.warehouse_label && (
                  <Badge variant="outline" className="mt-1 text-[10px] bg-white border-emerald-200 text-emerald-800">{product.warehouse_label}</Badge>
                )}
              </div>
            </div>
          ) : (
            <div className="flex gap-3 p-3 rounded-md border border-amber-300 dark:border-amber-900 bg-amber-50/70 dark:bg-amber-950/20" data-testid="warehouse-master-missing">
              <div className="h-12 w-12 rounded bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center text-amber-700">
                <AlarmClock className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0 text-xs">
                <div className="text-amber-800 dark:text-amber-300 font-semibold mb-0.5">Nu există imagine depozit</div>
                <div className="text-muted-foreground mb-1">
                  Pickerii vor vedea imaginea Shopify. Pentru o experiență vizuală mai bună, setează o foto reală a cutiei.
                </div>
                <Link
                  to={`/coletare/imagini?q=${encodeURIComponent(product.sku || product.title || product.id)}`}
                  className="text-amber-800 dark:text-amber-300 font-medium underline"
                  data-testid="warehouse-go-to-images"
                >
                  → Adaugă imagine în Warehouse Images
                </Link>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Cantitate</label>
              <Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="warehouse-new-quantity" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Prioritate</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                data-testid="warehouse-new-priority"
              >
                <option value="low">Mic</option>
                <option value="normal">Normal</option>
                <option value="high">Mare</option>
                <option value="urgent">URGENT</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Note (opțional)</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="warehouse-new-notes" />
          </div>

          <div className="flex gap-2">
            <Button onClick={submit} disabled={busy} data-testid="warehouse-create-submit">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Creează task
            </Button>
            <Button variant="ghost" onClick={onCancel}>Renunță</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function TvPanel({ tvConfig, onRotated }) {
  const [busy, setBusy] = useState(false);
  const tvUrl = useMemo(() => {
    if (!tvConfig?.token) return "";
    return `${window.location.origin}/warehouse-tv?token=${tvConfig.token}`;
  }, [tvConfig]);

  const rotate = async () => {
    if (!window.confirm("Rotește token-ul? Vechiul URL TV nu va mai funcționa.")) return;
    setBusy(true);
    try {
      const r = await rotateTvToken();
      onRotated?.(r);
      toast.success("Token rotit");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(tvUrl);
      toast.success("Link copiat");
    } catch { toast.error("Nu am putut copia"); }
  };

  return (
    <div className="space-y-3" data-testid="warehouse-tv-panel">
      <h3 className="text-sm font-semibold flex items-center gap-2"><Tv className="h-4 w-4 text-primary" /> Terminal TV</h3>
      <p className="text-xs text-muted-foreground">
        Deschide acest link pe TV-ul din depozit. Nu necesită login. Pickerii văd task-urile live și apasă „DONE" când le termină.
      </p>

      <div className="space-y-2">
        <label className="block text-[10px] uppercase tracking-wide text-muted-foreground">Link TV</label>
        <div className="flex gap-1">
          <Input value={tvUrl} readOnly className="text-xs" data-testid="warehouse-tv-url" />
          <Button size="icon" variant="outline" onClick={copy} title="Copiază"><Copy className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="outline" asChild title="Deschide într-un tab nou">
            <a href={tvUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={rotate} disabled={busy} className="w-full" data-testid="warehouse-tv-rotate">
          <RotateCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Rotește token
        </Button>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-xs text-amber-900 dark:text-amber-100">
        <AlarmClock className="h-3.5 w-3.5 inline mr-1" />
        Asigură-te că TV-ul este deschis în fullscreen pentru cel mai bun efect. Sunetele se vor activa automat după prima interacțiune.
      </div>
    </div>
  );
}
